import {useQuery} from '@tanstack/react-query'
import {useNavigate, useSearchParams} from 'react-router-dom'

import {toast} from 'react-toastify'
import {
  ComissionListSchemaResponse,
  CommissionDetailSchemaResponse,
  MerchantSchemaResponse,
  Responses,
  StoreContactTitleListSchemaResponse,
  StoreListSchemaResponse,
  StoreSchemaResponse,
  UserDetailSchemaResponse,
} from '../domain'
import {useMerchant, useStore} from '../hook'

import {useIntl} from 'react-intl'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import useRequestParams from 'src/feature/bite/hook/request'
import {ProviderResponse, ProviderSchemaResponse} from 'src/feature/provider/domain'

const endPoints = {
  Invoice: 'Payment/ListByStore',
  Contact: 'Store/GetStoreContact',
  Subscription: 'Subscription/ListByStore',
  Transaction: 'Transaction/ListByStore',
  Terminal: 'Terminal/GetTerminals',
  Optional: 'Store/StoreOptionalList',
  User: 'User/UserListByStore',
  UserDetail: 'User/Detail',
  Activities: 'Activities/ListByStore',
  // Commission: 'Store/StoreCommissionList',
  CommissionDetail: 'Commission/Detail',
  Commission: 'Commission/ListByStore',
  SmsEngagement: 'Store/StoreEngagementSMSList',
  PushEngagement: 'Store/StoreEngagementPushList',
  EmailEngagement: 'Store/StoreEngagementEmailList',
}

const typeOfFilterKey = {
  Invoice: 'Date',
  Contact: 'CreatedDate', // One Page
  Subscription: 'CreatedOn',
  Transaction: 'Date',
  Terminal: 'CreatedOn',
  Optional: 'CreatedOn',
  User: 'CreatedOn',
  UserDetail: 'CreatedOn',
  Commission: 'CreatedOn',
  Activities: 'CreatedOn',
  CommissionDetail: 'CreatedOn',
  SmsEngagement: 'CreatedOn',
  PushEngagement: 'CreatedOn',
  EmailEngagement: 'CreatedOn',
}

export function useGetList() {
  const intl = useIntl()
  const {auth, headers, signOut} = useRequestParams()
  const {listSet, listFilter, listFilterSet} = useStore()

  const userID = auth()?.id

  return useQuery(
    ['store', listFilter],
    async () => {
      const Response = await fetch(`${API}/Store/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = StoreListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreListSchemaResponse.parse(Data)
      listSet(parse.response)
      listFilterSet({
        ...listFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetailCorporate() {
  const router = useNavigate()
  const {detailSet} = useMerchant()
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  return useQuery(
    ['corporateDetail'],
    async () => {
      if (!merchantID) return true
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Merchant/GetMerchant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          id: merchantID,
          userID,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()
      const safeParse = MerchantSchemaResponse.safeParse(Data)
      const parse = MerchantSchemaResponse.parse(Data)

      if (!parse.response) {
        router('/error/404')
      }

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      let responseData = parse.response as any
      const uniqueInfoContentDataNames = await responseData?.infoContentData?.reduce(
        async (accPromise, item) => {
          const acc = await accPromise
          const existing = acc.find((i) => i.name === item.name)
          if (!existing) {
            acc.push(item)
          }
          return acc
        },
        Promise.resolve([])
      )

      responseData.infoContentData = uniqueInfoContentDataNames

      // Start: Get HTML Items
      if (responseData?.infoContentData) {
        const faqs = [
          {
            title: 'Additional Information',
          },
          {
            title: 'Culture & Values',
          },
        ]

        await Promise.all(
          responseData?.infoContentData?.map(async (item) => {
            const dataUrl = item?.contentURL
            if (dataUrl) {
              const res = await fetch(dataUrl, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${auth()?.token}`,
                },
              })
              const dataValue = await res.text()
              const parser = new DOMParser()
              const doc = parser.parseFromString(dataValue, 'text/html')
              const contentView = doc.querySelector('#contentView')

              if (contentView) {
                item.data = contentView.innerHTML || ''
              }
            }
            return item
          })
        )

        await Promise.all(
          faqs.map((faq) => {
            const item = responseData?.infoContentData?.find((item) => item.name === faq.title)
            if (!item) {
              responseData?.infoContentData?.push({
                name: faq.title,
                data: '',
              })
            }
          })
        )
      }

      // End: Get HTML Items

      // Start: Get Additional Info

      let data = responseData

      if (data?.additionalInformations) {
        const dataUrl = data?.additionalInformations

        if (dataUrl) {
          const res = await fetch(dataUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth()?.token}`,
            },
          })

          const dataValue = await res.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(dataValue, 'text/html')
          const contentView = doc.querySelector('#contentView')

          if (contentView) {
            data.additionalInformations = contentView.innerHTML
          } else data.additionalInformations = ''
        }
      }

      // End: Get Additional Info

      detailSet(parse.response)

      return parse.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetailTermsAndConditions() {
  const router = useNavigate()
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  return useQuery(
    ['getTermsAndConditionsOption'],
    async () => {
      if (!merchantID) return true
      const timeZoneOffset = getTimeZone()
      const merchantType = auth()?.merchantBusinessType

      // Check if merchantType is Corporate (1) or Provider (2)
      if (merchantType === 1) {
        const Response = await fetch(`${API}/Merchant/GetMerchant`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            language: headers.Language,
            id: merchantID,
            userID,
            timeZoneOffSet: timeZoneOffset,
          }),
        })

        if (Response.status === 401) signOut()

        const Data = await Response.json()
        const safeParse = MerchantSchemaResponse.safeParse(Data)
        const parse = MerchantSchemaResponse.parse(Data)

        if (!parse.response) {
          router('/error/404')
        }

        if (parse.responseResult.result === false) {
          toast.error(parse.responseResult.exceptionDetail || 'Error', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: 'light',
          })
          return false
        }

        if (!safeParse.success) {
          throw new Error(safeParse.error.message)
        }

        return parse.response?.termsAndConditionsEnabled || false
      } else if (merchantType === 2) {
        const Response = await fetch(`${API}/Provider/GetProvider`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userID,
            language: headers.Language,
            merchantID,
            timeZoneOffSet: timeZoneOffset,
          }),
        })

        if (Response.status === 401) {
          signOut()
        }

        const response: ProviderResponse = await Response.json()

        const parse = ProviderSchemaResponse.parse(response)

        if (parse.responseResult.result === false) {
          toast.error(parse.responseResult.exceptionDetail || 'Error', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: 'light',
          })
          return false
        }

        return parse.response?.termsAndConditionsEnabled || false
      }
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail(storeId: string = '') {
  const router = useNavigate()
  const {detailSet, recentTransactionsSet, updateDetail, tab} = useStore()
  const {auth, headers, errorMessage, signOut} = useRequestParams()

  const userID = auth()?.id
  return useQuery(
    ['storeDetail', storeId],
    async () => {
      if (!storeId) return true

      const Response = await fetch(`${API}/Store/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId,
          language: headers.Language,
          userID,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()
      const safeParse = StoreSchemaResponse.safeParse(Data)
      const parse = StoreSchemaResponse.parse(Data)

      if (!parse.response.store) {
        router('/error/store')
      }

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      detailSet(parse.response.store)

      const filter = {
        filterList: [],
        orderList: [{type: 2, key: 'Date'}],
        paging: {currentPageNumber: 1, pageSize: 5},
        search: {value: '', keyList: []},
      }

      const ContactResponse = await fetch(`${API}/Store/GetStoreContact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          storeId,
          ...filter,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      const Contact = await ContactResponse.json()

      if (Contact.responseResult.result === false) {
        return false
      }

      updateDetail(Contact.response, 'Contact')

      const OperationalDetailsResponse = await fetch(`${API}/Store/StoreOptionalList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          storeId,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      const OperationalDetails = await OperationalDetailsResponse.json()

      if (OperationalDetails.responseResult.result === false) {
        return false
      }

      updateDetail(OperationalDetails.response, 'Optional')

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetTabDetail(storeId: string) {
  const {detail, tab, updateDetail, tabFilter, tabFilterSet} = useStore()
  const {auth, headers, signOut, errorMessage} = useRequestParams()

  const currentPageNumber: number = detail?.[tab]?.paging?.currentPageNumber || 1
  const pageSize: number = detail?.[tab]?.paging?.pageSize || 10

  const apiEndPoint = endPoints[tab]
  const typeOfFilter = typeOfFilterKey[tab]

  const filter = {
    orderList: [{type: 2, key: typeOfFilter}],
    search: {value: '', keyList: []},
    // paging: {currentPageNumber, pageSize},
    ...tabFilter,
  }

  let engagementIndex: number

  if (tab === 'SmsEngagement') {
    engagementIndex = 1
  }
  if (tab === 'PushEngagement') {
    engagementIndex = 3
  }
  if (tab === 'EmailEngagement') {
    engagementIndex = 2
  }

  return useQuery(
    ['storeDetailTab', tab, filter],
    async () => {
      if (
        tab === 'Overview' ||
        tab === 'Engagement' ||
        tab === 'CommissionDetail' ||
        tab === 'Locations' ||
        tab === 'UserDetail' ||
        tab === 'Activities'
      )
        return true

      const Response = await fetch(`${API}/${apiEndPoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
          storeID: storeId || auth()?.merchantID,
          ...filter,
          ...(engagementIndex && {engagementType: engagementIndex}),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const ResponseSchema = Responses[tab]
      const safeParse = ResponseSchema.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(Data)

      if (parse.responseResult.result === false) {
        throw new Error(parse.responseResult.exceptionDetail || 'Error')
      }

      if (parse.response.paging) {
        tabFilterSet({
          ...tabFilter,
          paging: {
            currentPageNumber: parse.response.paging.currentPageNumber,
            pageSize: parse.response.paging.pageSize,
          },
        })
      }

      updateDetail(parse.response, tab)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCommissionDetail(storeId: string) {
  const intl = useIntl()
  // let {storeId} = useParams()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {commissionListFilter, comissionDetailSet, tabSet} = useStore()

  const userID = auth()?.id

  // if (!storeId) storeId = '996225251718'

  const commissionId = searchParams?.get('commissionId') || ''

  return useQuery(
    ['commission', commissionId],
    async () => {
      if (!commissionId || commissionId === '') {
        tabSet('Overview')
        return true
      }

      const Response = await fetch(`${API}/Commission/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...commissionListFilter,
          storeId,
          userID,
          commissionId,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = CommissionDetailSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = CommissionDetailSchemaResponse.parse(Data)

      comissionDetailSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetUserDetail(storeId: string) {
  const intl = useIntl()
  // let {storeId} = useParams()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {userDetailSet, tabSet} = useStore()

  const userID = auth()?.id

  const id = searchParams?.get('userID') || ''

  return useQuery(
    ['userDetail', id],
    async () => {
      if (!id || id === '') {
        tabSet('Overview')
        return true
      }

      const Response = await fetch(`${API}/User/Detail`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          storeId,
          id,
          userID,
          language: headers.Language,
          timeZoneOffSet: getTimeZone(),
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = UserDetailSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = UserDetailSchemaResponse.parse(Data)

      userDetailSet(parse.response)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCommissionList() {
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const {auth, headers, signOut} = useRequestParams()
  const {commissionListSet, commissionListFilter, commissionListFilterSet} = useStore()

  const userID = auth()?.id

  const CommissionId = searchParams?.get('commissionId') || ''

  return useQuery(
    ['commissionList', commissionListFilter],
    async () => {
      const Response = await fetch(`${API}/CommissionBracket/ListByCommission`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...commissionListFilter,
          userID,
          CommissionId,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = ComissionListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ComissionListSchemaResponse.parse(Data)
      commissionListSet(parse.response)
      commissionListFilterSet({
        ...commissionListFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetStoreContactTitleList() {
  const {auth, headers, signOut, errorMessage} = useRequestParams()
  const {storeContactTitleListSet} = useStore()

  return useQuery(
    ['storeContactTitleList'],
    async () => {
      const Response = await fetch(`${API}/Store/StoreContactTitleList`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          userID: auth()?.id || '',
          language: headers.Language,
          filterList: [],
          orderList: [],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      const safeParse = StoreContactTitleListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreContactTitleListSchemaResponse.parse(Data)
      storeContactTitleListSet(parse.response.storeContactTitleList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetContactTitleList(token: {userID: string; token: string}) {
  const {headersWithoutAuth, errorMessage, headers} = useRequestParams()
  const {storeContactTitleListSet} = useStore()

  return useQuery(
    ['storeContactTitleList', token],
    async () => {
      if (!token) return true

      const Response = await fetch(`${API}/Store/StoreContactTitleList`, {
        method: 'POST',
        headers: {
          ...headersWithoutAuth,
          Authorization: `Bearer ${token.token}`,
        },
        body: JSON.stringify({
          userID: token.userID,
          language: headers.Language,
          filterList: [],
          orderList: [],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const Data = await Response.json()

      const safeParse = StoreContactTitleListSchemaResponse.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = StoreContactTitleListSchemaResponse.parse(Data)
      storeContactTitleListSet(parse.response.storeContactTitleList)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetIsoList() {
  const intl = useIntl()
  const {auth, headers, signOut} = useRequestParams()
  const {isoListSet} = useStore()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {currentPageNumber: 1, pageSize: 1000},
    search: {value: '', keyList: []},
  }

  return useQuery(
    ['isolist', listFilter],
    async () => {
      const Response = await fetch(`${API}/ISO/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({...listFilter, userID, language: headers.Language}),
      })

      if (Response.status === 401) signOut()

      const Data = await Response.json()

      isoListSet(Data.response.dataList || [])

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(intl.formatMessage({id: 'ERROR'}), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
